<script setup>
import { computed, ref, watch } from 'vue';
const props = defineProps({
  body: {
    type: String,
    default: '',
  },
  templateParams: {
    type: Object,
    default: () => ({}),
  },
});

const processedParams = computed(
  () => props.templateParams?.processed_params || {}
);

const headerData = computed(() => processedParams.value.header || {});

console.debug('[BubbleWhatsappTemplate] mounting with params', props.templateParams);

const headerMedia = computed(() => {
  if (!headerData.value) return null;
  const type = headerData.value.media_type;
  if (headerData.value.media_url && type) {
    return { type: type.toLowerCase(), url: headerData.value.media_url };
  }
  if (headerData.value.media_id && type) {
    return { type: type.toLowerCase(), id: headerData.value.media_id };
  }
  return null;
});

const footerText = computed(() => {
  // Footer is stored at templateParams level (not in processed_params)
  return props.templateParams?.footer || '';
});

const buttons = computed(() => {
  // Buttons are stored at templateParams level (not in processed_params)
  if (Array.isArray(props.templateParams?.buttons)) {
    return props.templateParams.buttons;
  }
  return [];
});

const bodyContent = computed(() => {
  let text = props.body || '';
  // Convert <br>, <br/>, <br /> to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  // Remove HTML tags
  text = text.replace(/<[^>]*>/g, '');
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  // Normalize multiple newlines to single newline
  text = text.replace(/\n{2,}/g, '\n');
  // Trim extra whitespace
  return text.trim();
});

const getButtonIcon = button => {
  const type = button.type?.toUpperCase();
  if (type === 'URL' || button.url) return 'i-lucide-external-link';
  if (type === 'PHONE_NUMBER' || button.phone_number) return 'i-lucide-phone';
  if (type === 'COPY_CODE') return 'i-lucide-copy';
  return 'i-lucide-reply';
};

const getButtonText = button => {
  return button.text || button.title || button.parameter || button.payload || 'Botão';
};

const getButtonLink = button => {
  const type = button.type?.toUpperCase();
  if (type === 'URL' || button.url) {
    return button.url;
  }
  if (type === 'PHONE_NUMBER' || button.phone_number) {
    const phone = button.phone_number || button.url;
    return phone ? `tel:${phone}` : null;
  }
  return null;
};

const handleButtonClick = button => {
  const type = button.type?.toUpperCase();
  if (type === 'COPY_CODE') {
    const code = button.example || button.text || '';
    navigator.clipboard.writeText(code);
  }
};

const MAX_HEADER_SIZE = 330;
const MIN_HEADER_SIZE = 240;

const mediaBox = ref({
  width: MAX_HEADER_SIZE,
  height: MAX_HEADER_SIZE,
  objectFit: 'contain',
});

const computeMediaBox = (naturalWidth, naturalHeight) => {
  if (!naturalWidth || !naturalHeight) {
    return {
      width: MAX_HEADER_SIZE,
      height: MAX_HEADER_SIZE,
      objectFit: 'contain',
    };
  }

  const scaleToMax = Math.min(
    MAX_HEADER_SIZE / naturalWidth,
    MAX_HEADER_SIZE / naturalHeight,
    1
  );

  let width = naturalWidth * scaleToMax;
  let height = naturalHeight * scaleToMax;

  const isBelowMin = naturalWidth < MIN_HEADER_SIZE || naturalHeight < MIN_HEADER_SIZE;

  if (isBelowMin) {
    const scaleToMin = Math.max(
      MIN_HEADER_SIZE / width,
      MIN_HEADER_SIZE / height,
      1
    );

    width = Math.min(width * scaleToMin, MAX_HEADER_SIZE);
    height = Math.min(height * scaleToMin, MAX_HEADER_SIZE);
  }

  return {
    width: Math.max(Math.round(width), 1),
    height: Math.max(Math.round(height), 1),
    objectFit: isBelowMin ? 'cover' : 'contain',
  };
};

const updateMediaBox = ({ width, height }) => {
  mediaBox.value = computeMediaBox(width, height);
};

const onImageLoad = event => {
  updateMediaBox({
    width: event?.target?.naturalWidth,
    height: event?.target?.naturalHeight,
  });
};

const onVideoMetadata = event => {
  updateMediaBox({
    width: event?.target?.videoWidth,
    height: event?.target?.videoHeight,
  });
};

const mediaBoxStyle = computed(() => ({
  width: `${mediaBox.value.width}px`,
  height: `${mediaBox.value.height}px`,
}));

const mediaObjectFit = computed(() => mediaBox.value.objectFit);

const bubbleStyle = computed(() => {
  if (!headerMedia.value) return {};
  return {
    width: `${mediaBox.value.width}px`,
    maxWidth: `${MAX_HEADER_SIZE}px`,
  };
});

watch(
  () => headerMedia.value,
  () => {
    mediaBox.value = {
      width: MAX_HEADER_SIZE,
      height: MAX_HEADER_SIZE,
      objectFit: 'contain',
    };
  }
);
</script>

<template>
  <div class="wa-template-bubble" :style="bubbleStyle">
    <!-- Header com mídia -->
    <div v-if="headerMedia" class="wa-header">
      <div
        v-if="headerMedia.type === 'image'"
        class="wa-header-media-container"
        :style="mediaBoxStyle"
      >
        <img
          :src="headerMedia.url"
          alt="Header media"
          class="wa-header-media"
          :style="{ objectFit: mediaObjectFit }"
          @load="onImageLoad"
        />
      </div>
      <div
        v-else-if="headerMedia.type === 'video'"
        class="wa-header-media-container"
        :style="mediaBoxStyle"
      >
        <video
          :src="headerMedia.url"
          controls
          class="wa-header-media"
          :style="{ objectFit: mediaObjectFit }"
          @loadedmetadata="onVideoMetadata"
        />
      </div>
      <div v-else-if="headerMedia.type === 'document'" class="wa-header-document">
        <i class="i-lucide-file-text text-xl text-n-slate-11" />
        <span class="text-sm text-n-slate-11">Documento</span>
        <span v-if="headerMedia.id" class="text-xs text-n-slate-10">ID: {{ headerMedia.id }}</span>
      </div>
      <div v-else class="wa-header-placeholder">
        <i class="i-lucide-image text-xl text-n-slate-10" />
        <span>{{ headerMedia.type?.toUpperCase() || 'MÍDIA' }}</span>
        <span v-if="headerMedia.id" class="wa-header-id">ID: {{ headerMedia.id }}</span>
      </div>
    </div>

    <!-- Corpo do template -->
    <div v-if="bodyContent" class="wa-body">
      {{ bodyContent }}
    </div>

    <!-- Footer -->
    <div v-if="footerText" class="wa-footer">
      <span class="wa-footer-text">{{ footerText }}</span>
    </div>

    <!-- Botões -->
    <div v-if="buttons.length" class="wa-buttons">
      <a
        v-for="(button, index) in buttons"
        :key="index"
        :href="getButtonLink(button)"
        :target="button.type?.toUpperCase() === 'URL' ? '_blank' : undefined"
        rel="noopener noreferrer"
        class="wa-button"
        @click="handleButtonClick(button)"
      >
        <i :class="getButtonIcon(button)" class="wa-button-icon" />
        <span class="wa-button-text">{{ getButtonText(button) }}</span>
      </a>
    </div>
  </div>
</template>

<style>
/* === Override do .bubble pai (que aplica bg-woot-500 via bubble-with-types) === */
.bubble:has(.wa-template-bubble) {
  background-color: #D9FDD3 !important;
  color: #111B21 !important;
  overflow: hidden;
}

.dark .bubble:has(.wa-template-bubble) {
  background-color: #005C4B !important;
  color: #E9EDEF !important;
}

/* Timestamp / checkmarks com cores WhatsApp (sobrescreve o text-white do bubble-with-types) */
.bubble:has(.wa-template-bubble) .message-text--metadata .time {
  color: #667781 !important;
}

.dark .bubble:has(.wa-template-bubble) .message-text--metadata .time {
  color: #8696A0 !important;
}

/* === Layout interno do template === */
.wa-template-bubble {
  display: flex;
  flex-direction: column;
  /* Cancela o padding TOP+LATERAL do .bubble pai (py-2 px-4) — mantém bottom 0
     para não invadir o espaço do timestamp/checkmarks renderizados abaixo. */
  margin: -8px -16px 0 -16px;
  max-width: 360px;
  min-width: 240px;
  color: #111B21;
}

.dark .wa-template-bubble {
  color: #E9EDEF;
}

/* Header — mídia edge-to-edge */
.wa-header {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.wa-header-media-container {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  max-width: 360px;
  max-height: 360px;
}

.wa-header-media {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 360px;
  max-height: 360px;
}

.wa-header-document,
.wa-header-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 32px 16px;
  background-color: rgba(0, 0, 0, 0.04);
  color: #667781;
}

.dark .wa-header-document,
.dark .wa-header-placeholder {
  background-color: rgba(255, 255, 255, 0.06);
  color: #8696A0;
}

.wa-header-placeholder {
  font-size: 14px;
}

.wa-header-id {
  display: block;
  font-size: 12px;
  margin-top: 4px;
  opacity: 0.7;
}

/* Body — escala oficial do WhatsApp: 14.2px / 19px */
.wa-body {
  padding: 12px 12px 0 12px;
  font-size: 14.2px;
  line-height: 19px;
  white-space: pre-wrap;
}

/* Footer */
.wa-footer {
  padding: 4px 12px 6px 12px;
}

.wa-footer-text {
  font-size: 13px;
  color: #667781;
}

.dark .wa-footer-text {
  color: #8696A0;
}

/* Botões — texto colorido + divisor, sem fill */
.wa-buttons {
  display: flex;
  flex-direction: column;
  margin-top: 4px;
}

.wa-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #027EB5;
  text-decoration: none;
  cursor: pointer;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  transition: background-color 150ms ease;
}

.dark .wa-button {
  color: #53BDEB;
  border-top-color: rgba(255, 255, 255, 0.10);
}

.wa-button-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.wa-button-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wa-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.wa-button:active {
  background-color: rgba(0, 0, 0, 0.08);
}

.dark .wa-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.dark .wa-button:active {
  background-color: rgba(255, 255, 255, 0.10);
}
</style>
